/**
 * global-input-guard.ts
 *
 * P0-3: global safety net that enforces sane maxLength caps on every
 * <input> and <textarea> in the app, even ones that don't use the shared
 * Input/Textarea components. Prevents 50,000-char pastes from triggering
 * regex backtracking, re-render storms, or browser hangs.
 *
 * Strategy:
 *  - On every "input" event (capture phase, before React handlers), if the
 *    target is an <input> or <textarea> WITHOUT an explicit maxLength, we
 *    enforce a default cap (200 for input, 5000 for textarea).
 *  - If the value already exceeds the cap, we truncate it in place.
 *  - Errors are swallowed; under no circumstance should this handler crash
 *    the page.
 *
 * Inputs with type=checkbox / radio / button / submit / hidden / range /
 * file / image are skipped — they don't carry user-typed text.
 */

const INPUT_DEFAULT_MAX = 200;
const TEXTAREA_DEFAULT_MAX = 5000;
const HARD_CEILING = 10000; // Absolute ceiling regardless of attribute.

const SKIP_TYPES = new Set([
  "checkbox",
  "radio",
  "button",
  "submit",
  "reset",
  "hidden",
  "range",
  "file",
  "image",
  "color",
]);

function effectiveCap(el: HTMLInputElement | HTMLTextAreaElement): number {
  // If the element has an explicit, positive maxLength attribute (not -1 default),
  // respect it but still cap by HARD_CEILING to defeat enormous configured limits.
  const ml = el.maxLength;
  if (typeof ml === "number" && ml > 0) {
    return Math.min(ml, HARD_CEILING);
  }
  if (el instanceof HTMLTextAreaElement) return TEXTAREA_DEFAULT_MAX;
  return INPUT_DEFAULT_MAX;
}

function shouldGuard(el: EventTarget | null): el is HTMLInputElement | HTMLTextAreaElement {
  if (!el) return false;
  if (el instanceof HTMLTextAreaElement) return true;
  if (el instanceof HTMLInputElement) {
    const t = (el.type || "text").toLowerCase();
    if (SKIP_TYPES.has(t)) return false;
    return true;
  }
  return false;
}

let installed = false;

export function installGlobalInputGuard(): void {
  if (installed || typeof document === "undefined") return;
  installed = true;

  // Use capture phase so we run BEFORE React's synthetic event delegation
  // can dispatch the change into expensive state setters / validators.
  document.addEventListener(
    "input",
    (e) => {
      try {
        const t = e.target;
        if (!shouldGuard(t)) return;
        const cap = effectiveCap(t);
        const v = t.value;
        if (typeof v !== "string") return;
        if (v.length > cap) {
          // Truncate in place. React's onChange will then see the safe value.
          t.value = v.slice(0, cap);
        }
      } catch {
        // Never throw out of a capture-phase listener.
      }
    },
    true,
  );

  // Also intercept paste events — if the clipboard payload is enormous,
  // truncate it before the browser inserts it.
  document.addEventListener(
    "paste",
    (e) => {
      try {
        const t = e.target;
        if (!shouldGuard(t)) return;
        const cap = effectiveCap(t);
        const cb = (e as ClipboardEvent).clipboardData;
        if (!cb) return;
        const raw = cb.getData("text");
        if (typeof raw !== "string") return;
        // Predict the post-paste length and short-circuit huge pastes.
        // We don't preventDefault here for moderate pastes — the input event
        // handler above will catch overflow. Only act on outrageously big
        // payloads (>HARD_CEILING) to keep the browser responsive.
        if (raw.length > HARD_CEILING) {
          e.preventDefault();
          const safe = raw.slice(0, cap);
          // Re-insert truncated content using execCommand for broad compat.
          try {
            document.execCommand("insertText", false, safe);
          } catch {
            t.value = (t.value || "").slice(0, cap - safe.length) + safe;
            t.dispatchEvent(new Event("input", { bubbles: true }));
          }
        }
      } catch {
        // Swallow.
      }
    },
    true,
  );
}
